// ** Modules
import type { ReactNode } from 'react';

// ** UI
import Box from '@mui/material/Box';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';

// ** Components
import {
	default as NavCard,
	type NavCardModel,
	type NavGridColumns,
	NAV_GRID_DESKTOP_CARD_WIDTHS
} from '@/components/common/NavGrid/NavCard';

export type NavGridProps = {
	columns: NavGridColumns;
	children?: ReactNode;
	items?: NavCardModel[];
	listLabel?: string;
	sx?: SxProps<Theme>;
};

const GRID_GAP_PX = 32;

const NavGrid = ({ columns, children, items, listLabel, sx }: NavGridProps) => {
	const desktopCardWidth = NAV_GRID_DESKTOP_CARD_WIDTHS[columns];
	const desktopGridWidth = desktopCardWidth * columns + GRID_GAP_PX * (columns - 1);
	const content = items?.map((item) => <NavCard key={item.id} columns={columns} {...item} />) ?? children;
	const tabletTwoColMaxWidth = NAV_GRID_DESKTOP_CARD_WIDTHS[columns] * 2 + GRID_GAP_PX;

	const gridLayoutSx =
		columns === 4
			? {
					gridTemplateColumns: {
						xs: 'minmax(0, 1fr)',
						md: 'repeat(2, minmax(0, 1fr))'
					},
					maxWidth: {
						xs: 361,
						md: 648
					},
					'@media (min-width:1200px)': {
						gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
						maxWidth: `${desktopGridWidth}px`
					}
				}
			: columns === 3
				? {
						gridTemplateColumns: {
							xs: 'minmax(0, 1fr)',
							md: 'repeat(2, minmax(0, 1fr))'
						},
						maxWidth: {
							xs: 361,
							md: `${tabletTwoColMaxWidth}px`
						},
						'@media (min-width:1200px)': {
							gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
							maxWidth: `${desktopGridWidth}px`
						}
					}
				: {
						gridTemplateColumns: {
							xs: 'minmax(0, 1fr)',
							md: `repeat(${columns}, minmax(0, 1fr))`
						},
						maxWidth: { xs: 361, md: `${desktopGridWidth}px` }
					};

	return (
		<Box
			sx={[
				{
					py: { xs: '32px', md: '80px' }
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			<Box
				component="ul"
				aria-label={listLabel}
				sx={{
					display: 'grid',
					gap: '32px',
					listStyle: 'none',
					mx: 'auto',
					my: 0,
					p: 0,
					justifyItems: 'stretch',
					alignItems: 'stretch',
					'& > *': { width: '100%', minWidth: 0 },
					...gridLayoutSx
				}}
			>
				{content}
			</Box>
		</Box>
	);
};

export default NavGrid;
